import { useState } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
  Textarea,
  Tooltip,
  CopyButton,
} from "@repo/ui/core";
import {
  IconCheck,
  IconClipboard,
  IconPlus,
  IconTrash,
  IconUserPlus,
} from "@repo/ui/icons";
import { useParkingLot } from "@contexts/ParkingLotContext";
import type { ParkingLotItem } from "@data/types";

const STATUS_COLORS: Record<ParkingLotItem["status"], string> = {
  open: "blue",
  resolved: "green",
  "needs-followup": "orange",
};

const STATUS_LABELS: Record<ParkingLotItem["status"], string> = {
  open: "Open",
  resolved: "Resolved",
  "needs-followup": "Follow-up",
};

export function ParkingLot() {
  const { items, addItem, resolveItem, setFollowup, removeItem, exportMarkdown } =
    useParkingLot();
  const [newText, setNewText] = useState("");
  const [filter, setFilter] = useState("all");
  const [followupId, setFollowupId] = useState<string | null>(null);
  const [followupName, setFollowupName] = useState("");
  const [followupEmail, setFollowupEmail] = useState("");
  const [followupNotes, setFollowupNotes] = useState("");

  const filtered = items.filter((item) => {
    if (filter === "all") return true;
    return item.status === filter;
  });

  const handleAdd = () => {
    const trimmed = newText.trim();
    if (!trimmed) return;
    addItem(trimmed);
    setNewText("");
  };

  const handleFollowup = (id: string) => {
    if (!followupName.trim()) return;
    setFollowup(id, {
      name: followupName.trim(),
      email: followupEmail.trim() || undefined,
      notes: followupNotes.trim() || undefined,
    });
    setFollowupId(null);
    setFollowupName("");
    setFollowupEmail("");
    setFollowupNotes("");
  };

  return (
    <Stack gap="lg">
      <Group>
        <TextInput
          placeholder="Add a question or action item..."
          value={newText}
          onChange={(e) => setNewText(e.currentTarget.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
          style={{ flex: 1 }}
          size="md"
        />
        <Button
          onClick={handleAdd}
          leftSection={<IconPlus size={16} />}
          style={{ backgroundColor: "var(--customer-accent)" }}
        >
          Add
        </Button>
      </Group>

      <Group justify="space-between">
        <SegmentedControl
          value={filter}
          onChange={setFilter}
          data={[
            { label: `All (${items.length})`, value: "all" },
            {
              label: `Open (${items.filter((i) => i.status === "open").length})`,
              value: "open",
            },
            {
              label: `Follow-up (${items.filter((i) => i.status === "needs-followup").length})`,
              value: "needs-followup",
            },
            {
              label: `Resolved (${items.filter((i) => i.status === "resolved").length})`,
              value: "resolved",
            },
          ]}
          size="sm"
        />
        <CopyButton value={exportMarkdown()}>
          {({ copied, copy }) => (
            <Button
              variant="subtle"
              size="sm"
              leftSection={
                copied ? <IconCheck size={14} /> : <IconClipboard size={14} />
              }
              onClick={copy}
            >
              {copied ? "Copied!" : "Export Markdown"}
            </Button>
          )}
        </CopyButton>
      </Group>

      <Stack gap="sm">
        {filtered.length === 0 && (
          <Text c="dimmed" ta="center" py="xl">
            {filter === "all"
              ? "No items yet. Add questions or action items above."
              : `No ${filter} items.`}
          </Text>
        )}
        {filtered.map((item) => (
          <Card
            key={item.id}
            padding="md"
            radius="sm"
            style={{
              background: "rgba(255,255,255,0.02)",
              borderLeft: `3px solid var(--mantine-color-${STATUS_COLORS[item.status]}-6)`,
            }}
          >
            <Group justify="space-between" wrap="nowrap" align="flex-start">
              <Stack gap={4} style={{ flex: 1 }}>
                <Group gap="sm">
                  <Badge color={STATUS_COLORS[item.status]} size="sm" variant="light">
                    {STATUS_LABELS[item.status]}
                  </Badge>
                  <Text size="xs" c="dimmed">
                    {new Date(item.createdAt).toLocaleString()}
                  </Text>
                </Group>
                <Text size="sm">{item.text}</Text>
                {item.followup && (
                  <Text size="xs" c="dimmed">
                    → {item.followup.name}
                    {item.followup.email && ` (${item.followup.email})`}
                    {item.followup.notes && ` — ${item.followup.notes}`}
                  </Text>
                )}
              </Stack>
              <Group gap={4}>
                {item.status === "open" && (
                  <>
                    <Tooltip label="Mark resolved">
                      <ActionIcon
                        variant="subtle"
                        color="green"
                        onClick={() => resolveItem(item.id)}
                      >
                        <IconCheck size={16} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Needs follow-up">
                      <ActionIcon
                        variant="subtle"
                        color="orange"
                        onClick={() => setFollowupId(item.id)}
                      >
                        <IconUserPlus size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </>
                )}
                <Tooltip label="Remove">
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    onClick={() => removeItem(item.id)}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Group>

            {followupId === item.id && (
              <Stack gap="sm" mt="md" p="sm" style={{ background: "rgba(255,255,255,0.02)", borderRadius: 4 }}>
                <TextInput
                  placeholder="Assigned to (name/team)"
                  value={followupName}
                  onChange={(e) => setFollowupName(e.currentTarget.value)}
                  size="sm"
                />
                <TextInput
                  placeholder="Email (optional)"
                  value={followupEmail}
                  onChange={(e) => setFollowupEmail(e.currentTarget.value)}
                  size="sm"
                />
                <Textarea
                  placeholder="Notes (optional)"
                  value={followupNotes}
                  onChange={(e) => setFollowupNotes(e.currentTarget.value)}
                  size="sm"
                  rows={2}
                />
                <Group>
                  <Button size="sm" onClick={() => handleFollowup(item.id)} style={{ backgroundColor: "var(--customer-accent)" }}>
                    Assign Follow-up
                  </Button>
                  <Button size="sm" variant="subtle" onClick={() => setFollowupId(null)}>
                    Cancel
                  </Button>
                </Group>
              </Stack>
            )}
          </Card>
        ))}
      </Stack>
    </Stack>
  );
}
